import apiClient, { ApiResponse } from "./client";
import { config } from "../config";
import { notify } from "../utils/toast";

export interface MasterDegree {
	id: string;
	majorCode: string; // Mã Ngành
	major: string; // Ngành
	cccd: string; // CCCD
	studentId: string; // Mã sinh viên
	studentName: string; // Họ và tên
	dateOfBirth: string; // Ngày sinh
	placeOfBirth: string; // Nơi sinh
	gender: string; // Giới tính
	ethnicCode: string; // Mã Dân tộc
	ethnicity: string; // Dân tộc
	nationalityCode: string; // Mã quốc tịch
	nationality: string; // quốc tịch
	graduationYear: string; // Năm tốt nghiệp
	classification: string; // Xếp loại tốt nghiệp
	classificationId: string; // Xếp loại tốt nghiệp ID
	trainingMode: string; // Hình thức đào tạo
	degreeNumber: string; // Số hiệu bằng (cơ sở)
	registrationNumber: string; // Số vào sổ cấp bằng
	signedBy: string; // Người ký
	signerTitle: string; // Chức danh người ký
	signDate?: string; // Ngày ký
	course: string; // Khoá học
	recognitionDecisionNumber: string; // Quyết định công nhận số
	requestTypeId: string; // Loại yêu cầu ID
	requestType: string; // Loại yêu cầu
	thesisTitle?: string; // Đề tài luận văn
	supervisor?: string; // Giảng viên hướng dẫn
	notes?: string; // Ghi chú
	defenseDate?: string; // Ghi chú
	status: "Nội bộ" | "Đang gửi" | "Đã xác nhận" | "Đã thu hồi"; // Trạng thái hệ thống
	createdAt?: string;
	updatedAt?: string;
}

export interface MasterDegreeFilters {
	search?: string;
	requestType?: string;
	trainingMode?: string;
	major?: string;
	yearFrom?: string;
	defenseDateFrom?: string;
	defenseDateTo?: string;
	yearTo?: string;
	status?: "Nội bộ" | "Đang gửi" | "Đã xác nhận" | "Đã thu hồi";
	page?: number;
	limit?: number;
}

export interface MasterDegreeListResponse {
	data: MasterDegree[];
	total: number;
	page: number;
	totalPages: number;
	limit: number;
}

export interface BulkImportResponse {
	created: MasterDegree[];
	count: number;
	errors?: { row: number; message: string }[];
}

export const masterDegreesApi = {
	count: async ()=> {
		return await apiClient.get<number>(`${config.endpoints.masterDegrees}/count`);
	},
	// Get all master degrees with optional filters
	getAll: async (filters?: MasterDegreeFilters): Promise<ApiResponse<MasterDegreeListResponse>> => {
		try {
			const response = await apiClient.get<MasterDegreeListResponse>(config.endpoints.masterDegrees, filters);
			return response;
		} catch (error) {
			console.error("Error fetching master degrees:", error);
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error;
		}
	},

	// Get master degree by ID
	getById: async (id: string, field?: string, log: boolean = true): Promise<ApiResponse<MasterDegree>> => {
		try {
			const response = await apiClient.get<MasterDegree>(`${config.endpoints.masterDegrees}/${id}?field=${field}`);
			return response;
		} catch (error) {
			console.error("Error fetching master degree:", error);
			if (error instanceof Error && log) {
				notify.error(error.message);
			}
			throw error;
		}
	},

	// Create new master degree
	create: async (degree: Omit<MasterDegree, "id" | "createdAt" | "updatedAt">): Promise<ApiResponse<MasterDegree>> => {
		try {
			const response = await apiClient.post<MasterDegree>(config.endpoints.masterDegrees, degree);
			return response;
		} catch (error) {
			console.error("Error creating master degree:", error);
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error;
		}
	},

	// Update master degree
	update: async (id: string, degree: Partial<MasterDegree>): Promise<ApiResponse<MasterDegree>> => {
		try {
			const response = await apiClient.put<MasterDegree>(`${config.endpoints.masterDegrees}/${id}`, degree);
			return response;
		} catch (error) {
			console.error("Error updating master degree:", error);
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error;
		}
	},

	// Delete master degree
	delete: async (id: string): Promise<ApiResponse<void>> => {
		try {
			const response = await apiClient.delete<void>(`${config.endpoints.masterDegrees}/${id}`);
			return response;
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error;
		}
	},

	// Bulk import master degrees
	bulkImport: async (degrees: Omit<MasterDegree, "id" | "createdAt" | "updatedAt">[]): Promise<ApiResponse<BulkImportResponse>> => {
		try {
			const response = await apiClient.post<BulkImportResponse>(`${config.endpoints.masterDegrees}/bulk-import`, degrees);
			return response;
		} catch (error) {
			console.error("Error bulk importing master degrees:", error);
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error;
		}
	},

	// Export master degrees
	export: async (filters?: MasterDegreeFilters): Promise<ApiResponse<MasterDegree[]>> => {
		try {
			const response = await apiClient.get<MasterDegree[]>(`${config.endpoints.masterDegrees}/export`, filters);
			return response;
		} catch (error) {
			console.error("Error exporting master degrees:", error);
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error;
		}
	},

	// Search public master degrees (for public search page)
	searchPublic: async (query: { cccd?: string; studentId?: string; degreeNumber?: string }): Promise<ApiResponse<MasterDegree[]>> => {
		try {
			const response = await apiClient.get<MasterDegree[]>(`${config.endpoints.public.search}/master-degrees`, query);
			return response;
		} catch (error) {
			console.error("Error searching master degrees:", error);
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error;
		}
	},

	checkExist: async ({ searchValue, field }: { searchValue: string; field: "degreeNumber" | "studentId" }) => {
		const response = await apiClient.get<boolean>(`${config.endpoints.masterDegrees}/check-exist`, { search_value: searchValue, field });
		return response;
	},

	checkCitizenInfoExist: async ({ citizenId, majorCode }: { citizenId: string; majorCode: string }) => {
		const response = await apiClient.get<boolean>(`${config.endpoints.masterDegrees}/check-citizen-exist`, { search_value: citizenId, major_code: majorCode });
		return response;
	},
};
